import React, { Component, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import history from 'utils/history'
import { FormBody, FormFooter, Row, Col, Button, Label, FormGroup, SimpleAlert, TabsList, Tabs, TabsTrigger, TabsContent, FormCard } from 'twyne-new.ui'
import { kebabCase } from 'lodash'
import { LoaderCircle, Play } from 'lucide-react'

import Formsy from '@Components/form-new/FormContainer'
import Switch from 'components/form/Switch'
import Input from 'components/form/Input'
import Select from 'components/form/Select'
import InfoTip from 'components/InfoTip'

import MappingBuilder from './mapping-builder'
import ParamItem, { STATIC_OPTION } from './ParamItem'
import HeaderItem from './HeaderItem'
import TestItem from './TestItem'
import SelectCreatable from './SelectCreatable'
import Auth from './Auth'
import { toJS } from 'mobx'
import TestTabs from './TestTabs'
import CodeEditor from '@/components/form/CodeEditor'

export default class FormComponent extends Component {
  state = {}

  form = React.createRef()

  componentWillMount() {
    const data = this.props.data
    if (data) {
      this.setState(toJS(data))
    }
  }

  handleCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    } else {
      history.goBack();
    }
  }

  handleValidSubmit = (model, isTest) => {
    return this.props.handleSubmit(Object.assign({}, this.state, model), isTest);
  }

  useUrl = () => {
    const [url, setUrl] = useState(this.state.url || '')
    return { url, setUrl }
  }

  useParams = ({ url, setUrl }) => {
    const [searchParams, setSearchParams] = useState(null)
    const [items, setItems] = useState([])

    useEffect(() => {
      try {
        setSearchParams(url ? new URL(url).searchParams : null)
      } catch (error) {
        setSearchParams(url ? new URLSearchParams(url) : null)
      }
    }, [url])

    useEffect(() => {
      setItems(searchParams ? [...searchParams.entries()] : [])
    }, [searchParams])

    const update = (items) => {
      const searchParams = new URLSearchParams()
      for (let [key, value] of items) {
        searchParams.append(key, value)
      }

      const search = searchParams.toString()
      try {
        const u = new URL(url)
        u.search = search
        setUrl(decodeURI(u.href))
      } catch (error) {
        setUrl(decodeURI(search))
      }
    }

    return { update, items }
  }

  useHeaders = () => {
    const { headers } = this.state.request_config || {}
    const [items, setItems] = useState([])
    const [count, setCount] = useState(items.length)

    useEffect(() => {
      headers && setItems(Object.entries(headers))
    }, [headers])

    const update = (items) => {
      setItems(items)
      setCount(items.length)
    }

    return { update, items }
  }

  useBody = (contentType) => {
    const { body } = this.state.request_config || {}

    const { multipart, json, urlencoded, plain, xml } = CONTENT_TYPES

    const isStructured = useMemo(() => [multipart, urlencoded].includes(contentType), [contentType])
    const isText = useMemo(() => [json, plain, xml].includes(contentType), [contentType])

    const [text, setText] = useState('')
    const [structured, setStructured] = useState([])

    const update = useCallback(data => {
      if (isStructured) updateStructured(data)
      else if (isText) setText(data)
    }, [isStructured, isText])

    const updateStructured = data => {
      const structured = Array.isArray(data) ? data : Object.keys(data).map(key => [key, `${data[key]}`])
      setStructured(structured)
    }

    const updateTextToStructured = useCallback(() => {
      try {
        const data = JSON.parse(text)
        updateStructured(data)
      } catch (error) { }
    }, [text])

    const updateStructuredToText = useCallback(() => {
      const object = Array.isArray(structured) ? structured.reduce((object, [key, value]) => ({ ...object, [key]: value }), {}) : structured
      const text = JSON.stringify(object, null, 2)
      setText(text)
    }, [structured])

    useEffect(() => { isStructured && updateTextToStructured() }, [isStructured])
    useEffect(() => { isText && updateStructuredToText() }, [isText])
    useEffect(() => { update(body) }, [body])

    return {
      update,
      isStructured, isText,
      data: isStructured ? structured : text
    }
  }

  URLInput = ({ url: { url, setUrl } }) => {
    return <Input required
      name="url"
      value={url}
      onChange={e => setUrl(e.target.value)}
    />
  }

  Details = ({ children: url_input }) => {
    const { name, slug, active = true, method } = this.state
    const [autoSlug, setAutoSlug] = useState(false)
    const [slugs, setSlugs] = useState([])

    useEffect(() => {
      const autoSlug = !slug
      autoSlug && setAutoSlug(autoSlug)
    }, [!!slug])

    const loadSlugs = async () => {
      const slugs = await this.props.loadSlugs()
      setSlugs(slugs)
    }
    useEffect(() => { loadSlugs() }, [])

    const handleChangeName = e => {
      const name = e.target.value
      const state = { name }
      if (autoSlug)
        state.slug = kebabCase(name)
      this.setState(state)
    }

    const handleChangeSlug = e => {
      const slug = e.target.value
      this.setState({ slug })
      setAutoSlug(false)
    }

    return <Row>
      <Col size="6">
        <FormGroup>
          <Label>Name</Label>
          <Input
            name="name"
            value={name}
            onChange={handleChangeName}
            required
          />
        </FormGroup>
      </Col>

      <Col size="6">
        <FormGroup>
          <Label>Slug</Label>
          <Input
            validations={{ isSlugUnique: (_, slug) => slugs && !slugs.includes(slug) }}
            validationErrors={{ isSlugUnique: "The slug must be unique." }}
            name="slug"
            value={slug}
            onChange={handleChangeSlug}
            required
          />
        </FormGroup>
      </Col>
      <Col size="3">
        <FormGroup>
          <Label>Method</Label>
          <Select required
            name="method"
            options={METHODS_OPTIONS}
            value={method}
            handleOnChange={item => this.setState({ method: item.value })}
            clearable={false}
          />
        </FormGroup>
      </Col>
      <Col size="9">
        <FormGroup>
          <Label>URL</Label>
          {url_input}
        </FormGroup>
      </Col>
    </Row>
  }

  General = () => {
    const { timeout = 3000, cache_ttl = 60 * 5 } = this.state.request_config || {}
    return <FormCard title='General'>
      <Row>
        <Col size="6">
          <FormGroup>
            <Label>Request Timeout <small>(Milliseconds)</small></Label>
            <Input type='number' required
              name="request_config.timeout"
              value={timeout}
            />
          </FormGroup>
        </Col>
        <Col size="6">
          <FormGroup>
            <Label>
              Cache TTL <small>(Seconds)</small>
              <InfoTip color='accent'>Time to keep cached the external API response.</InfoTip>
            </Label>
            <Input type='number' required
              name="request_config.cache_ttl"
              value={cache_ttl}
            />
          </FormGroup>
        </Col>
      </Row>
    </FormCard>
  }

  BodyParamItem = ({ data, ...props }) => {
    const [key, _, ignore] = data
    return <ParamItem {...props}
      data={data}
      name={ignore ? '_' : `request_config[body][${key}]`} />
  }

  ItemsBuilder = ({ data, onChange, component: ItemComponent }) => {
    const handleAdd = (key = '', value = '') => {
      data.push([key, value])
      onChange(data)
    }

    const handleChange = idx => (key, value) => {
      data[idx] = [key, value]
      onChange(data)
    }

    const handleDelete = idx => () => {
      data.splice(idx, 1)
      onChange(data)
    }

    return <Row>
      <Col size='12'>
        <Row>
          {[...data, ['', '', true]].map((item, idx) => <Col size='12' key={idx}>
            <ItemComponent
              data={item}
              index={idx}
              onDelete={idx !== data.length ? handleDelete(idx) : null}
              onChange={idx !== data.length ? handleChange(idx) : handleAdd}
            />
          </Col>)}
        </Row>
      </Col>
    </Row>
  }

  Params = ({ url }) => {
    const { items, update } = this.useParams(url)

    const createParamOption = label => {
      label = label.replace(/^{(\w+)}$/, '$1')
      return {
        label,
        value: `{${label}}`
      }
    }

    return <SelectCreatable.Provider createOption={createParamOption} options={[STATIC_OPTION]}>
      <this.ItemsBuilder
        data={items}
        onChange={update}
        component={ParamItem}
      />
    </SelectCreatable.Provider>
  }

  Headers = () => {
    const { items, update } = this.useHeaders()

    return <SelectCreatable.Provider options={HEADER_OPTIONS}>
      <this.ItemsBuilder
        data={items}
        onChange={update}
        component={HeaderItem}
      />
    </SelectCreatable.Provider>
  }

  Auth = () => {
    const { request_config = {} } = this.state
    return <Auth data={request_config.auth} />
  }

  StructuredBody = ({ data: { isStructured, data, update } }) => {

    if (!isStructured)
      return null

    const createParamOption = (label) => {
      label = label.replace(/^{(\w+)}$/, '$1')
      return {
        label,
        value: `{${label}}`
      }
    }

    return <SelectCreatable.Provider createOption={createParamOption} options={[STATIC_OPTION]}>
      <this.ItemsBuilder
        data={data}
        onChange={(value) => update([...value])}
        component={this.BodyParamItem}
      />
    </SelectCreatable.Provider>
  }

  TextBody = ({ data: { isText, data, update }, contentType }) => {

    if (!isText)
      return null

    const [mode, setMode] = useState('')

    useEffect(() => {
      if (contentType === CONTENT_TYPES.json)
        setMode('json')
      else {
        if (contentType === CONTENT_TYPES.plain)
          setMode('plain_text')
        if (contentType === CONTENT_TYPES.xml)
          setMode('xml')
      }
    }, [contentType])

    return <CodeEditor
      name="request_config.body"
      key={mode}
      language={mode}
      value={data}
      onChange={update}
      enableCreateAutocompletion={true}
    />
  }

  Body = () => {
    const { content_type } = this.state.request_config || {}

    const [contentType, setContentType] = useState(content_type)

    useEffect(() => {
      setContentType(content_type ? content_type : CONTENT_TYPES.json)
    }, [content_type])

    const body = this.useBody(contentType)

    return <Row>
      <Col size="12">
        <FormGroup>
          <Label>Content Type</Label>
          <Select
            name="request_config.content_type"
            options={CONTENT_TYPE_OPTIONS}
            value={contentType}
            handleOnChange={item => setContentType(item.value)}
            clearable={false}
          />
        </FormGroup>
      </Col>
      <Col size="12">
        <FormGroup>
          <Label>Body</Label>
          <this.TextBody data={body} contentType={contentType} />
          <this.StructuredBody data={body} />
        </FormGroup>
      </Col>
    </Row>
  }

  Mapping = () => <FormCard title='Mapping'>
    <MappingBuilder
      tree={this.state.mapping}
      onChange={mapping => this.setState({ mapping })}
    />
  </FormCard>

  Test = () => {
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(null)
    const [result, setResult] = useState({})
    const [firstRender, setFirstRender] = useState(false)

    const { form } = this
    const { currentForm } = this.state
    const mergetags = useMemo(() => {
      const mergetags = []
      const lookupMergetags = content => {
        for (let [_, key] of content.matchAll(/{(\w+)}/g)) {
          if (!mergetags.includes(key))
            mergetags.push(key)
        }
      }

      if (form.current) {
        const { url, request_config: { headers, body } } = form.current.getModel()
        const { method } = this.state
        lookupMergetags(url)

        if (body && method === 'post') {
          lookupMergetags(typeof body === 'string' ? body : JSON.stringify(body))
        }

        if (headers)
          for (let [hkey, hvalue] of Object.entries(headers)) {
            hkey && lookupMergetags(hkey)
            hvalue && lookupMergetags(hvalue)
          }
      }

      return mergetags
    })

    useEffect(() => {
      if (success !== null) {
        this.setState({ needTest: !success })
      }
    }, [success])

    const handleTest = async () => {
      setLoading(true)
      setSuccess(null)
      const result = await this.handleValidSubmit(this.form.current.getModel(), true)
      setResult(result)
      setSuccess(result?.datasourceOptions?.success)
      setLoading(false)
    }

    const firstRenderRef = useRef(firstRender)
    useEffect(() => {
      firstRenderRef.current = firstRender
    }, [firstRender])

    const mapping = this.state.mapping && JSON.stringify(this.state.mapping)
    const requestKey = 'request_config.auth'
    const headerKey = 'request_config[headers]'
    const configuration = useMemo(() => currentForm && JSON.stringify(
      Object.entries(currentForm)
        .filter(([key]) => key.startsWith(requestKey) || key.startsWith(headerKey) || key === 'url')
        .map(([key, value]) => ({ [key]: value })),
    ), [currentForm])

    useEffect(() => {
      if (!configuration) {
        return
      }

      if (!firstRenderRef.current) {
        setFirstRender(true)
        return
      }

      this.setState({ needTest: true })
    }, [configuration, mapping])

    return <>
      <FormCard title='Test' headerButtons={<>
        <Button color='accent' onClick={handleTest} disabled={loading} size='sm'>
          {loading ? <>
            <LoaderCircle size={16} className='animate-spin' />
            Running
          </> : <>
            <Play size={16} />
            Run Test
          </>}
        </Button>
      </>}>
        <Row>
          <Col size='12'>
            <TestTabs datasourceOptions={result.datasourceOptions} request={result.request} response={result.response} />
          </Col>
          <Col size='12'>
            <Input name="request_config.mergetags" value={mergetags} type='hidden' />
            {success !== null && !success ? <SimpleAlert className='text-center' variant='danger'>{result?.datasourceOptions?.error || 'This mapping appear to be invalid.'}</SimpleAlert> : null}
            {success !== null && !!success ? <SimpleAlert variant='success' className='text-center'>Looks good!</SimpleAlert> : null}
            {mergetags.map(mergetag => <TestItem key={`${mergetag[0]}`} mergetag={mergetag} />)}
          </Col>
        </Row>
      </FormCard>
    </>
  }

  Tabs = ({ url }) => <>
    <Tabs defaultValue='params' forceMount size='sm'>
      <TabsList>
        <TabsTrigger value='params'>Params</TabsTrigger>
        <TabsTrigger value='headers'>Headers</TabsTrigger>
        {this.state.method === 'post' ? <TabsTrigger value='body'>Body</TabsTrigger> : null}
      </TabsList>
      <TabsContent value='params'>
        <this.Params url={url} />
      </TabsContent>
      <TabsContent value='headers'>
        <this.Headers />
      </TabsContent>
      <TabsContent value='body'>
        <this.Body />
      </TabsContent>
    </Tabs>
  </>

  Content = () => {
    const url = this.useUrl()

    return <>
      <Row>
        <Col size="12">
          <FormCard title='Details'>
            <this.Details>
              <this.URLInput url={url} />
            </this.Details>
            <this.Tabs url={url} />
          </FormCard>
        </Col>
        <Col size="6">
          <this.General />
        </Col>
        <Col size="6">
          <this.Auth />
        </Col>
        <Col size="12">
          <this.Mapping />
        </Col>
        <Col size="12">
          <this.Test />
        </Col>
      </Row>
    </>
  }

  render() {
    return <Formsy onValidSubmit={model => this.handleValidSubmit(model)} innerRef={this.form} onChange={form => this.setState({ currentForm: form })}>
      <FormBody>
        <this.Content />
        <FormFooter>
          <Button type='submit' color="accent">Save</Button>
          <Button onClick={this.handleCancel} variant='ghost'>Cancel</Button>
        </FormFooter>
      </FormBody>
    </Formsy>
  }
}

FormComponent.propTypes = {
  onCancel: PropTypes.func,
  handleSubmit: PropTypes.func,
  data: PropTypes.any
};

const METHODS_OPTIONS = [
  { value: 'get', label: 'GET' },
  { value: 'post', label: 'POST' }
]

const HEADER_OPTIONS = ['A-IM', 'Accept', 'Accept-Charset', 'Accept-Datetime', 'Accept-Encoding', 'Accept-Language', 'Access-Control-Request-Method', 'Access-Control-Request-Headers', 'Authorization', 'Cache-Control', 'Connection', 'Content-Encoding', 'Content-Length', 'Content-MD5', 'Content-Type', 'Cookie', 'Date', 'Expect', 'Forwarded', 'From', 'Host', 'HTTP2-Settings', 'If-Match', 'If-Modified-Since', 'If-None-Match', 'If-Range', 'If-Unmodified-Since', 'Max-Forwards', 'Origin', 'Pragma', 'Prefer', 'Proxy-Authorization', 'Range', 'Referer', 'TE', 'Trailer', 'Transfer-Encoding', 'User-Agent', 'Upgrade', 'Via', 'Warning']
  .map(value => ({ value, label: value }))
  .concat(STATIC_OPTION)

const CONTENT_TYPES = {
  json: 'application/json',
  plain: 'text/plain',
  multipart: 'multipart/form-data',
  urlencoded: 'application/x-www-form-urlencoded',
  xml: 'application/xml',
}

const CONTENT_TYPE_OPTIONS = [
  { label: 'application/json', value: CONTENT_TYPES.json },
  { label: 'text/plain', value: CONTENT_TYPES.plain },
  { label: 'multipart/form-data', value: CONTENT_TYPES.multipart },
  { label: 'application/x-www-form-urlencoded', value: CONTENT_TYPES.urlencoded },
  { label: 'application/xml', value: CONTENT_TYPES.xml },
]
